import { supabase } from '../supabaseClient';
import { loadStripe } from '@stripe/stripe-js';

// Load Stripe.js outside of component renders
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

export const SUBSCRIPTION_TIERS = {
  FREE: {
    name: 'free',
    credits: 50,
    features: ['Basic image generation', 'Community gallery access']
  },
  BASIC: {
    name: 'basic',
    credits: 500,
    features: ['Advanced image generation', 'Priority support', 'HD downloads']
  },
  PRO: {
    name: 'pro',
    credits: 2000,
    features: ['Unlimited image generation', '24/7 support', '4K downloads', 'API access']
  }
};

export const getSubscriptionDetails = async (userId) => {
  const { data, error } = await supabase
    .from('profiles')
    .select(`
      subscription_tier,
      subscription_status,
      subscription_start,
      subscription_end,
      credits_remaining,
      stripe_subscription_id
    `)
    .eq('id', userId)
    .single();

  if (error) {
    console.error('Error fetching subscription details:', error);
    return null;
  }

  return {
    ...data,
    features: SUBSCRIPTION_TIERS[data.subscription_tier?.toUpperCase()]?.features || SUBSCRIPTION_TIERS.FREE.features
  };
};

// Initiates Stripe Checkout
export const redirectToCheckout = async (priceId, userId) => {
  try {
    // 1. Call your backend to create the Checkout Session
    const response = await fetch('/api/create-checkout-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ priceId, userId }),
    });

    if (!response.ok) {
      // Try to parse as JSON, but handle case where response is not JSON
      try {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create checkout session');
      } catch (jsonError) {
        // If JSON parsing fails or body stream already read, use status text
        throw new Error(
          `Failed to create checkout session: ${response.statusText || 'Server error'}`
        );
      }
    }

    const { sessionId } = await response.json();

    // 2. Redirect to Stripe Checkout
    const stripe = await stripePromise;
    if (!stripe) {
      throw new Error('Stripe.js failed to load.');
    }

    const { error } = await stripe.redirectToCheckout({ sessionId });

    if (error) {
      console.error('Stripe redirect error:', error);
      throw error; // Throw error to be caught by the calling component
    }
  } catch (error) {
    console.error('Checkout process error:', error);
    throw error; // Re-throw for UI handling
  }
};

// Cancels the Stripe subscription via your backend
export const cancelStripeSubscription = async (userId) => {
  try {
    const response = await fetch('/api/cancel-stripe-subscription', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }), // Send userId to backend
    });

    if (!response.ok) {
      // Try to parse as JSON, but handle case where response is not JSON
      try {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to cancel subscription');
      } catch (jsonError) {
        // If JSON parsing fails or body stream already read, use status text
        throw new Error(
          `Failed to cancel subscription: ${response.statusText || 'Server error'}`
        );
      }
    }

    return await response.json(); // Return success message/data
  } catch (error) {
    console.error('Error cancelling subscription:', error);
    throw error; // Re-throw for UI handling
  }
};

// (Optional) Redirects to Stripe Customer Portal
export const redirectToCustomerPortal = async (userId) => {
  try {
    const response = await fetch('/api/create-customer-portal-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      // Try to parse as JSON, but handle case where response is not JSON
      try {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create portal session');
      } catch (jsonError) {
        // If JSON parsing fails or body stream already read, use status text
        throw new Error(
          `Failed to create portal session: ${response.statusText || 'Server error'}`
        );
      }
    }

    const { url } = await response.json();
    window.location.href = url; // Redirect the user

  } catch (error) {
    console.error('Customer portal error:', error);
    throw error; // Re-throw for UI handling
  }
};
